import React, { useEffect, useState } from "react";

const createId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const normalizeApartments = (apartments = []) => {
  if (!Array.isArray(apartments)) return [];

  return apartments.map((apartment) => ({
    ...apartment,
    id: apartment.id || createId(),
    name: apartment.name || "",
    selected: apartment.selected ?? true,
    rooms: Array.isArray(apartment.rooms)
      ? apartment.rooms.map((room) => ({
          ...room,
          id: room.id || createId(),
          name: room.name || "",
        }))
      : [],
  }));
};

export default function HuoneistoKortitBlock({
  data = {},
  onChange,
  onCreateApartments,
}) {
  /*
   * Local editing prevents the whole report editor
   * from updating on every keypress.
   */
  const [apartments, setApartments] = useState(() =>
    normalizeApartments(data?.apartments)
  );

  const [amount, setAmount] = useState(
    Array.isArray(data?.apartments)
      ? data.apartments.length
      : 0
  );

  /*
   * Load saved data when the block/report is loaded.
   */
  useEffect(() => {
    if (
      Array.isArray(data?.apartments) &&
      data.apartments.length > 0 &&
      apartments.length === 0
    ) {
      const normalized = normalizeApartments(
        data.apartments
      );

      setApartments(normalized);
      setAmount(normalized.length);
    }
  }, [data?.apartments, apartments.length]);

  /*
   * Save apartments + rooms to block.data.
   */
  const saveApartments = (nextApartments) => {
    onChange?.({
      ...data,
      apartments: nextApartments,
    });
  };

  /*
   * Change apartment amount.
   */
  const handleAmountChange = (value) => {
    const newAmount = Math.max(
      0,
      Math.min(200, Number(value) || 0)
    );

    setAmount(newAmount);

    setApartments((current) => {
      let next = [...current];

      if (newAmount > current.length) {
        for (
          let i = current.length;
          i < newAmount;
          i++
        ) {
          next.push({
            id: createId(),
            name: "",
            selected: true,
            rooms: [],
          });
        }
      } else if (newAmount < current.length) {
        next = next.slice(0, newAmount);
      }

      saveApartments(next);

      return next;
    });
  };

  /*
   * Edit apartment name locally.
   */
  const updateApartmentNameLocal = (
    apartmentId,
    value
  ) => {
    setApartments((current) =>
      current.map((apartment) =>
        apartment.id === apartmentId
          ? {
              ...apartment,
              name: value,
            }
          : apartment
      )
    );
  };

  /*
   * Select/unselect apartment.
   */
  const updateSelected = (
    apartmentId,
    selected
  ) => {
    setApartments((current) => {
      const next = current.map((apartment) =>
        apartment.id === apartmentId
          ? {
              ...apartment,
              selected,
            }
          : apartment
      );

      saveApartments(next);

      return next;
    });
  };

  /*
   * Add room under ONE apartment.
   */
  const addRoom = (apartmentId) => {
    setApartments((current) => {
      const next = current.map((apartment) => {
        if (apartment.id !== apartmentId) {
          return apartment;
        }

        const rooms = Array.isArray(apartment.rooms)
          ? apartment.rooms
          : [];

        return {
          ...apartment,
          rooms: [
            ...rooms,
            {
              id: createId(),
              name: "",
            },
          ],
        };
      });

      saveApartments(next);

      return next;
    });
  };

  /*
   * Edit room name locally.
   */
  const updateRoomNameLocal = (
    apartmentId,
    roomId,
    value
  ) => {
    setApartments((current) =>
      current.map((apartment) => {
        if (apartment.id !== apartmentId) {
          return apartment;
        }

        return {
          ...apartment,
          rooms: (apartment.rooms || []).map(
            (room) =>
              room.id === roomId
                ? {
                    ...room,
                    name: value,
                  }
                : room
          ),
        };
      })
    );
  };

  /*
   * Delete room.
   */
  const removeRoom = (
    apartmentId,
    roomId
  ) => {
    setApartments((current) => {
      const next = current.map((apartment) => {
        if (apartment.id !== apartmentId) {
          return apartment;
        }

        return {
          ...apartment,
          rooms: (
            apartment.rooms || []
          ).filter(
            (room) => room.id !== roomId
          ),
        };
      });

      saveApartments(next);

      return next;
    });
  };

  /*
   * Save text fields when leaving an input.
   */
  const handleBlur = () => {
    saveApartments(apartments);
  };

  const selectAll = () => {
    const next = apartments.map(
      (apartment) => ({
        ...apartment,
        selected: true,
      })
    );

    setApartments(next);
    saveApartments(next);
  };

  const clearAll = () => {
    const next = apartments.map(
      (apartment) => ({
        ...apartment,
        selected: false,
      })
    );

    setApartments(next);
    saveApartments(next);
  };

  /*
   * Generate actual report blocks.
   */
  const handleCreate = () => {
    saveApartments(apartments);

    const selected = apartments
      .filter(
        (apartment) =>
          apartment.selected &&
          apartment.name?.trim()
      )
      .map((apartment) => ({
        ...apartment,

        name: apartment.name.trim(),

        rooms: (
          apartment.rooms || []
        )
          .filter((room) =>
            room.name?.trim()
          )
          .map((room) => ({
            ...room,
            name: room.name.trim(),
          })),
      }));

    if (selected.length === 0) {
      window.alert(
        "Valitse vähintään yksi nimetty huoneisto."
      );

      return;
    }

    /*
     * Every selected apartment must have
     * at least one room.
     */
    const apartmentWithoutRooms =
      selected.find(
        (apartment) =>
          apartment.rooms.length === 0
      );

    if (apartmentWithoutRooms) {
      window.alert(
        `Lisää vähintään yksi huone huoneistolle ${apartmentWithoutRooms.name}.`
      );

      return;
    }

    onCreateApartments?.(selected);
  };

  const selectedCount =
    apartments.filter(
      (apartment) => apartment.selected
    ).length;

  return (
    <div className="card">
      <div className="card-body">
        <h5 className="mb-3">
          Huoneistokortit
        </h5>

        {/* APARTMENT AMOUNT */}
        <div className="mb-4">
          <label className="form-label fw-bold">
            Tarkastettujen huoneistojen määrä
          </label>

          <input
            type="number"
            min="0"
            max="200"
            className="form-control"
            style={{
              maxWidth: "150px",
            }}
            value={amount}
            onChange={(e) =>
              handleAmountChange(
                e.target.value
              )
            }
          />
        </div>

        {apartments.length > 0 && (
          <>
            {/* HEADER */}
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>
                Huoneistot
              </strong>

              <div>
                <button
                  type="button"
                  className="btn btn-link btn-sm"
                  onClick={selectAll}
                >
                  Valitse kaikki
                </button>

                <button
                  type="button"
                  className="btn btn-link btn-sm text-secondary"
                  onClick={clearAll}
                >
                  Tyhjennä valinnat
                </button>
              </div>
            </div>

            {/* APARTMENTS */}
            <div className="d-flex flex-column gap-3">
              {apartments.map(
                (apartment, index) => {
                  const rooms =
                    Array.isArray(
                      apartment.rooms
                    )
                      ? apartment.rooms
                      : [];

                  return (
                    <div
                      key={apartment.id}
                      className="border rounded"
                      style={{
                        overflow: "hidden",
                      }}
                    >
                      {/* APARTMENT ROW */}
                      <div
                        className="d-flex align-items-center gap-3 p-3"
                        style={{
                          background:
                            "#f8f9fa",
                        }}
                      >
                        <input
                          type="checkbox"
                          className="form-check-input mt-0"
                          checked={
                            apartment.selected ??
                            true
                          }
                          onChange={(e) =>
                            updateSelected(
                              apartment.id,
                              e.target.checked
                            )
                          }
                        />

                        <span
                          className="text-muted"
                          style={{
                            minWidth:
                              "30px",
                          }}
                        >
                          {index + 1}.
                        </span>

                        <div className="flex-grow-1">
                          <label
                            className="form-label mb-1 fw-semibold"
                            style={{
                              fontSize:
                                "0.75rem",
                            }}
                          >
                            Huoneisto
                          </label>

                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder={`Esim. A${
                              index + 1
                            }`}
                            value={
                              apartment.name ||
                              ""
                            }
                            onChange={(e) =>
                              updateApartmentNameLocal(
                                apartment.id,
                                e.target.value
                              )
                            }
                            onBlur={
                              handleBlur
                            }
                          />
                        </div>
                      </div>

                      {/* ROOMS */}
                      <div className="p-3 border-top">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div>
                            <strong
                              style={{
                                fontSize:
                                  "0.85rem",
                              }}
                            >
                              Huoneet
                            </strong>

                            <div
                              className="text-muted"
                              style={{
                                fontSize:
                                  "0.72rem",
                              }}
                            >
                              Jokaisesta
                              huoneesta luodaan
                              oma
                              lämpökamerakortti.
                            </div>
                          </div>

                          <button
                            type="button"
                            className="btn btn-sm btn-outline-success"
                            onClick={() =>
                              addRoom(
                                apartment.id
                              )
                            }
                          >
                            <i className="bi bi-plus-lg me-1" />
                            Lisää huone
                          </button>
                        </div>

                        {/* EMPTY */}
                        {rooms.length ===
                          0 && (
                          <div
                            className="text-muted border rounded p-3 text-center"
                            style={{
                              fontSize:
                                "0.8rem",
                              background:
                                "#fafafa",
                            }}
                          >
                            Ei huoneita.
                            Paina{" "}
                            <strong>
                              + Lisää huone
                            </strong>
                            .
                          </div>
                        )}

                        {/* ROOM ROWS */}
                        {rooms.length > 0 && (
                          <div className="d-flex flex-column gap-2">
                            {rooms.map(
                              (
                                room,
                                roomIndex
                              ) => (
                                <div
                                  key={
                                    room.id
                                  }
                                  className="d-flex align-items-end gap-2"
                                >
                                  <div
                                    className="text-success fw-bold pb-2"
                                    style={{
                                      width:
                                        "20px",
                                    }}
                                  >
                                    ↳
                                  </div>

                                  <div className="flex-grow-1">
                                    <label
                                      className="form-label mb-1"
                                      style={{
                                        fontSize:
                                          "0.72rem",
                                      }}
                                    >
                                      Huone{" "}
                                      {roomIndex +
                                        1}
                                    </label>

                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      placeholder={
                                        roomIndex ===
                                        0
                                          ? "Esim. Olohuone"
                                          : roomIndex ===
                                            1
                                          ? "Esim. Makuuhuone"
                                          : "Esim. Kylpyhuone"
                                      }
                                      value={
                                        room.name ||
                                        ""
                                      }
                                      onChange={(
                                        e
                                      ) =>
                                        updateRoomNameLocal(
                                          apartment.id,
                                          room.id,
                                          e.target
                                            .value
                                        )
                                      }
                                      onBlur={
                                        handleBlur
                                      }
                                    />
                                  </div>

                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    style={{
                                      height:
                                        "31px",
                                    }}
                                    title="Poista huone"
                                    onClick={() =>
                                      removeRoom(
                                        apartment.id,
                                        room.id
                                      )
                                    }
                                  >
                                    <i className="bi bi-trash" />
                                  </button>
                                </div>
                              )
                            )}
                          </div>
                        )}

                        {rooms.length > 0 && (
                          <div
                            className="text-muted mt-2"
                            style={{
                              fontSize:
                                "0.72rem",
                            }}
                          >
                            {rooms.length}{" "}
                            {rooms.length ===
                            1
                              ? "huonekortti"
                              : "huonekorttia"}{" "}
                            luodaan.
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }
              )}
            </div>

            {/* FOOTER */}
            <div className="d-flex justify-content-between align-items-center mt-3">
              <small className="text-muted">
                {selectedCount} /{" "}
                {apartments.length}{" "}
                valittu
              </small>

              <button
                type="button"
                className="btn btn-success"
                onClick={handleCreate}
              >
                <i className="bi bi-plus-lg me-2" />
                Luo huoneistokortit
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}